/* eslint-disable camelcase */
import type AkAvatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { type NewCoreIconProps } from '@atlaskit/icon';
import PersonIcon from '@atlaskit/icon/core/person';
import { type IconProps } from '@atlaskit/icon/types';
import { type BackgroundColor, Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import React, { type ComponentProps, type ComponentType, useState } from 'react';
import { type JSX } from 'react/jsx-runtime';

type CustomAvatarProps = {
	src?: string;
	size?: number;
	alt?: string;
};

type AvatarJoiningLineProps = {
	joiningLineHeight?: number;
};

const styles = cssMap({
	root: {
		borderRadius: token('radius.full'),
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	personIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		scale: 1.5,
	},
	boxAroundBadge: {
		paddingTop: token('space.025'),
		// We want the badge to be 12px, but there is no 12px icon size, so we'll have to
		// scale the 16px one down to 12px
		scale: 0.75,
	},
});

const AvatarJoiningLine = ({ joiningLineHeight = 0 }: AvatarJoiningLineProps) => (
	<div
		style={{
			backgroundColor: token('color.border'),
			width: '3px',
			left: '25.75px',
			height: joiningLineHeight + 'px',
			position: 'absolute',
		}}
	/>
);

const CustomAvatar = ({ src, size, alt }: CustomAvatarProps) => {
	const [imageError, setImageError] = useState(false);

	return (
		<Box xcss={styles.root} style={{ width: `${size}px`, height: `${size}px` }}>
			{src && !imageError ? (
				<img
					src={src}
					alt={alt || ''}
					onError={() => setImageError(true)}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
					}}
				/>
			) : (
				<Box xcss={styles.personIcon}>
					<PersonIcon label={alt || 'Default avatar'} />
				</Box>
			)}
		</Box>
	);
};

// CSS for the additional padding around the badge icon tile overlay since that component
// can't have a custom border radius
const badgeIconStyles = {
	position: 'absolute',
	right: token('space.0'),
	bottom: token('space.0'),
	width: token('space.300'),
	height: token('space.300'),
	zIndex: 'flag',
	borderRadius: token('radius.full'),
	alignItems: 'center',
	justifyContent: 'center',
} as const;

type AvatarProps = {
	badgeIcon?: {
		icon: ComponentType<IconProps | NewCoreIconProps>;
		color: BackgroundColor;
		label: string;
	};
	size?: number;
	avatarLineStartElementId?: string;
	avatarLineEndElementId?: string;
	joiningLineHeight?: number;
} & Omit<ComponentProps<typeof AkAvatar>, 'size'>;

export const Avatar = ({
	src,
	badgeIcon,
	size = 54,
	avatarLineStartElementId,
	avatarLineEndElementId,
	joiningLineHeight,
	...props
}: AvatarProps): JSX.Element => {
	return (
		<div style={{ position: 'relative', width: 'fit-content' }}>
			<CustomAvatar src={src} {...props} size={size} />
			<Flex
				xcss={xcss({
					...badgeIconStyles,
					...(badgeIcon?.color && {
						backgroundColor: badgeIcon.color,
					}),
				})}
			>
				{badgeIcon && (
					<Box xcss={styles.boxAroundBadge}>
						{React.createElement(badgeIcon.icon, {
							label: badgeIcon.label || '',
							color: token('color.text.inverse'),
						})}
					</Box>
				)}
			</Flex>
			{avatarLineStartElementId && avatarLineEndElementId && (
				<AvatarJoiningLine joiningLineHeight={joiningLineHeight} />
			)}
		</div>
	);
};
